package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document

/**
 * Self-contained premium styling for the subscription screen: injected once,
 * plain CSS so we can do things the Compose modifier layer can't (animated
 * gradient borders, conic glow, checklist leaders). Mirrors the landing's
 * pricing aesthetic (Unbounded display font, emerald brand, warm surfaces).
 */
@Composable
internal fun InjectSubscriptionStyles() {
    LaunchedEffect(Unit) {
        if (document.getElementById("qk-sub-fonts") == null) {
            document.createElement("link").also {
                it.setAttribute("id", "qk-sub-fonts")
                it.setAttribute("rel", "stylesheet")
                it.setAttribute(
                    "href",
                    "https://fonts.googleapis.com/css2?family=Unbounded:wght@500;600;700;800&display=swap",
                )
                document.head?.appendChild(it)
            }
        }
        val style = document.getElementById("qk-sub-styles") ?: document.createElement("style").also {
            it.setAttribute("id", "qk-sub-styles")
            document.head?.appendChild(it)
        }
        style.textContent = SUB_CSS
    }
}

// language=CSS
private const val SUB_CSS = """
.qk-sub{--e:#0F9D6E;--ed:#0B7A55;--mint:#7EE2BC;width:100%}
.qk-sub-disp{font-family:'Unbounded',system-ui,sans-serif;letter-spacing:-.02em}

/* ---- header ---- */
.qk-sub-head{margin-bottom:22px}
.qk-sub-eyebrow{display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:600;color:var(--ed);
  background:rgba(15,157,110,.1);border-radius:999px;padding:5px 12px}
.qk-sub-eyebrow::before{content:'';width:6px;height:6px;border-radius:50%;background:var(--e);box-shadow:0 0 0 3px rgba(15,157,110,.2)}
.qk-sub-title{font-size:27px;font-weight:800;color:#14201B;margin:12px 0 4px}
.qk-sub-sub{font-size:14px;color:#6B7280;line-height:1.5;max-width:520px}

/* ---- shared feature strip ---- */
.qk-sub-feat{display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:10px 22px;
  background:#fff;border:1px solid #ECE8E2;border-radius:18px;padding:18px 22px;margin-top:28px;margin-bottom:22px}
.qk-sub-feat .cap{grid-column:1/-1;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#9CA3AF;margin-bottom:2px}
.qk-sub-feat .f{display:flex;align-items:center;gap:9px;font-size:13.5px;color:#374151}
.qk-sub-feat .f i{flex:none;width:20px;height:20px;border-radius:50%;background:rgba(15,157,110,.12);color:var(--ed);
  display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-style:normal;font-weight:800}

/* ---- plan grid: two cards (Free/Pro), centered ---- */
.qk-sub-plans{display:grid;grid-template-columns:repeat(2,minmax(0,280px));gap:16px;margin-bottom:8px;justify-content:center}
@media(max-width:760px){.qk-sub-plans{grid-template-columns:1fr}}
.qk-sub-plan{position:relative;background:#fff;border:1.5px solid #ECE8E2;border-radius:20px;padding:22px 20px;cursor:pointer;
  transition:transform .3s cubic-bezier(.22,.61,.36,1),box-shadow .3s,border-color .2s,background .2s;overflow:hidden}
.qk-sub-plan:hover{transform:translateY(-4px);box-shadow:0 22px 44px -20px rgba(31,36,33,.22)}
.qk-sub-plan.sel{border-color:var(--e);background:linear-gradient(180deg,rgba(15,157,110,.06),transparent 60%);
  box-shadow:0 18px 40px -18px rgba(15,157,110,.5)}
.qk-sub-plan.best{border:none}
.qk-sub-plan.best::before{content:'';position:absolute;inset:0;border-radius:20px;padding:1.6px;
  background:conic-gradient(from var(--a,0deg),var(--e),var(--mint),#34D399,var(--e));
  -webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);-webkit-mask-composite:xor;
  mask-composite:exclude;animation:qk-spin-border 5s linear infinite;pointer-events:none}
@property --a{syntax:'<angle>';inherits:false;initial-value:0deg}
@keyframes qk-spin-border{to{--a:360deg}}
.qk-sub-plan .ribbon{position:absolute;top:14px;right:-30px;transform:rotate(45deg);background:linear-gradient(135deg,var(--e),var(--ed));
  color:#fff;font-size:10px;font-weight:800;letter-spacing:.05em;padding:4px 34px;box-shadow:0 4px 10px rgba(15,157,110,.35)}
.qk-sub-plan .radio{width:18px;height:18px;border-radius:50%;border:2px solid #D7D2CA;transition:.2s;flex:none}
.qk-sub-plan.sel .radio{border-color:var(--e);background:var(--e);box-shadow:inset 0 0 0 3px #fff}
.qk-sub-plan .name{font-size:14px;font-weight:700;color:#14201B}
.qk-sub-plan .price{display:flex;align-items:baseline;gap:5px;margin-top:12px}
.qk-sub-plan .price b{font-family:'Unbounded',sans-serif;font-size:30px;font-weight:700;color:#14201B;line-height:1}
.qk-sub-plan.sel .price b{color:var(--ed)}
.qk-sub-plan .price span{font-size:13px;color:#9CA3AF}
.qk-sub-plan .per{font-size:12.5px;color:#6B7280;margin-top:5px}
.qk-sub-plan .save{display:inline-block;font-size:11px;font-weight:800;color:#0B7A55;background:rgba(15,157,110,.12);
  border-radius:999px;padding:4px 10px;margin-top:12px}

/* ---- CTA ---- */
.qk-sub-cta{width:100%;height:52px;border:none;border-radius:14px;cursor:pointer;margin-top:18px;
  display:inline-flex;align-items:center;justify-content:center;gap:9px;color:#fff;font-size:15.5px;font-weight:700;
  background:linear-gradient(135deg,var(--e),var(--ed));box-shadow:0 14px 30px -12px rgba(15,157,110,.65);
  transition:transform .15s ease,box-shadow .3s,filter .2s;font-family:inherit}
.qk-sub-cta:hover{filter:brightness(1.04);box-shadow:0 18px 40px -12px rgba(15,157,110,.75)}
.qk-sub-cta:active{transform:scale(.985)}
.qk-sub-cta .lock{font-size:14px;opacity:.9}
.qk-sub-spin{width:20px;height:20px;border:2.4px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:qk-rot .8s linear infinite}
@keyframes qk-rot{to{transform:rotate(360deg)}}

/* ---- trust row ---- */
.qk-sub-trust{display:flex;flex-wrap:wrap;gap:8px 20px;justify-content:center;margin-top:16px}
.qk-sub-trust .t{display:flex;align-items:center;gap:6px;font-size:12.5px;color:#9CA3AF}
.qk-sub-trust .t i{color:var(--e);font-style:normal;font-weight:800}

/* ---- active membership card ---- */
.qk-mcard{position:relative;overflow:hidden;border-radius:22px;padding:26px;color:#fff;margin-bottom:16px;
  background:linear-gradient(135deg,#12A574 0%,#0B7A55 60%,#0A6647 100%);box-shadow:0 22px 50px -20px rgba(11,122,85,.75)}
.qk-mcard::after{content:'';position:absolute;top:-30px;right:-20px;width:220px;height:180px;pointer-events:none;opacity:.5;
  background-image:radial-gradient(rgba(255,255,255,.55) 2px,transparent 2.4px);background-size:14px 14px;
  -webkit-mask-image:linear-gradient(215deg,#000,transparent 78%);mask-image:linear-gradient(215deg,#000,transparent 78%)}
.qk-mcard .over{font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.78)}
.qk-mcard .row{display:flex;align-items:center;justify-content:space-between;position:relative;z-index:1}
.qk-mcard .plan{font-family:'Unbounded',sans-serif;font-size:26px;font-weight:700;margin-top:8px}
.qk-mcard .chip{background:rgba(255,255,255,.18);border-radius:999px;padding:6px 13px;font-size:12px;font-weight:700;
  letter-spacing:.04em;backdrop-filter:blur(4px)}
.qk-mcard .next{font-size:13.5px;color:rgba(255,255,255,.9);margin-top:8px}
.qk-mcard .acts{display:flex;align-items:center;justify-content:space-between;margin-top:22px;position:relative;z-index:1}
.qk-mcard .btn{background:#fff;color:var(--ed);border:none;border-radius:11px;padding:10px 18px;font-size:13px;font-weight:700;
  cursor:pointer;font-family:inherit;transition:transform .15s,box-shadow .2s}
.qk-mcard .btn:hover{box-shadow:0 8px 18px rgba(0,0,0,.18)}
.qk-mcard .btn:active{transform:scale(.96)}
.qk-mcard .link{color:rgba(255,255,255,.85);font-size:13px;cursor:pointer;text-decoration:underline;text-decoration-style:dotted;text-underline-offset:3px}
.qk-mcard .link:hover{color:#fff}

.qk-sub-warn{background:#fff;border:1px solid rgba(224,91,76,.35);border-radius:18px;padding:20px;margin-bottom:16px;
  display:flex;align-items:center;justify-content:space-between;gap:14px}
.qk-sub-warn .txt{font-size:14.5px;font-weight:600;color:#C4453A}
.qk-sub-warn .badge{background:rgba(224,91,76,.14);color:#C4453A;border-radius:999px;padding:6px 13px;font-size:12px;font-weight:700;flex:none}

/* ---- history ---- */
.qk-sub-hist{background:#fff;border:1px solid #ECE8E2;border-radius:18px;padding:18px 20px;margin-top:20px}
.qk-sub-hist .h{font-size:13px;font-weight:700;color:#14201B;margin-bottom:12px}
.qk-sub-hist .r{display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-top:1px solid #F1EDE7;font-size:13px}
.qk-sub-hist .r:first-of-type{border-top:none}
.qk-sub-hist .r .d{color:#4B5563}
.qk-sub-hist .r .a{display:flex;align-items:center;gap:9px}
.qk-sub-hist .r .amt{font-weight:700;color:#14201B}
.qk-sub-hist .pill{font-size:10.5px;font-weight:800;padding:3px 8px;border-radius:999px;letter-spacing:.03em}
.qk-sub-hist .pill.ok{background:rgba(15,157,110,.12);color:#0B7A55}
.qk-sub-hist .pill.no{background:rgba(224,91,76,.12);color:#C4453A}
.qk-sub-hist .pill.mid{background:#F1EDE7;color:#6B7280}
"""
